<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { useI18n } from 'vue-i18n';
import conversationProductsAPI from 'dashboard/api/conversationProducts';
import catalogProductsAPI from 'dashboard/api/catalogProducts';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency: accountCurrency } = useCurrency();

// tp = tradução com fallback PT fixo. Se o chunk de locale estiver velho em
// cache (i18n devolve a própria chave), cai no texto embutido no componente —
// que sempre vem fresco. Evita ver "PRODUCT.QUANTITY" cru na tela.
const tp = (key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const allConversations = useMapGetter('conversations/getAllConversations');

const loading = ref(false);
const saving = ref(false);
const showForm = ref(false);
const editingProduct = ref(null);
const products = ref([]);
const catalogProducts = ref([]);

const emptyForm = () => ({
  name: '',
  description: '',
  amount: 0,
  currency: accountCurrency.value,
  funnel_id: null,
  stage_name: null,
  product_id: null,
  quantity: 1,
  recurrence: 'one_time',
});

const form = ref(emptyForm());

const recurrenceLabels = computed(() => ({
  one_time: tp('PRODUCT.RECURRENCE.ONE_TIME', 'Único'),
  monthly: tp('PRODUCT.RECURRENCE.MONTHLY', 'Mensal (MRR)'),
  yearly: tp('PRODUCT.RECURRENCE.YEARLY', 'Anual'),
}));

const recurrenceSuffix = recurrence => {
  if (recurrence === 'monthly') return '/mês';
  if (recurrence === 'yearly') return '/ano';
  return '';
};

const fetchCatalog = async () => {
  try {
    const { data } = await catalogProductsAPI.get({ active: true });
    catalogProducts.value = data;
  } catch (error) {
    catalogProducts.value = [];
  }
};

const formatCurrency = (amount, currencyCode = accountCurrency.value) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currencyCode || accountCurrency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount || 0);
};

const recomputeFromCatalog = () => {
  const chosen = catalogProducts.value.find(
    p => p.id === Number(form.value.product_id)
  );
  if (!chosen) return;
  form.value.amount =
    Math.round(
      (parseFloat(chosen.unit_price) || 0) * (form.value.quantity || 1) * 100
    ) / 100;
};

// Ao escolher um produto do catálogo, copia nome/preço/recorrência.
const onCatalogSelect = () => {
  const chosen = catalogProducts.value.find(
    p => p.id === Number(form.value.product_id)
  );
  if (!chosen) return;
  form.value.name = chosen.name;
  form.value.recurrence = chosen.recurrence;
  form.value.description = chosen.description || form.value.description;
  recomputeFromCatalog();
};

const onQuantityChange = () => recomputeFromCatalog();

const fetchProducts = async () => {
  loading.value = true;
  try {
    const response = await conversationProductsAPI.get(props.conversationId);
    products.value = response.data;
  } catch (error) {
    useAlert(t('PRODUCT.API_ERROR.FETCH'));
  } finally {
    loading.value = false;
  }
};

const funnels = computed(() => store.getters['funnels/getFunnels'] || []);
const loadingFunnels = computed(() => store.getters['funnels/isLoading']);

const fetchFunnels = async () => {
  try {
    await store.dispatch('funnels/get');
  } catch (error) {
    // silencioso — funil é opcional
  }
};

const currentConversation = computed(() =>
  allConversations.value?.find(c => c.id === Number(props.conversationId))
);

const getMappingForFunnel = funnelId =>
  currentConversation.value?.funnel_mappings?.find(
    m => m.funnel_id === Number(funnelId)
  );

const getCurrentFunnelId = () => {
  const mappingList = currentConversation.value?.funnel_mappings;
  return mappingList?.length > 0 ? mappingList[0].funnel_id : null;
};

// Etapas do funil escolhido no formulário (ordenadas por posição).
const funnelStages = computed(() => {
  if (!form.value.funnel_id) return [];
  return store.getters['funnels/getOrderedStages'](
    Number(form.value.funnel_id)
  );
});

// Ao trocar o funil, pré-seleciona a etapa atual da conversa nesse funil
// (ou a etapa padrão do funil, se a conversa ainda não estiver nele).
const onFunnelSelect = () => {
  const mapping = getMappingForFunnel(form.value.funnel_id);
  if (mapping) {
    form.value.stage_name = mapping.stage_name;
    return;
  }
  const funnel = funnels.value.find(f => f.id === Number(form.value.funnel_id));
  form.value.stage_name = funnel?.settings?.default_stage || null;
};

const resetForm = () => {
  form.value = { ...emptyForm(), funnel_id: getCurrentFunnelId() };
  onFunnelSelect();
  editingProduct.value = null;
};

const openAdd = () => {
  resetForm();
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const updateConversationInStore = () => {
  const conversations = store.getters['conversations/getAllConversations'];
  const conversation = conversations?.find(
    c => c.id === Number(props.conversationId)
  );
  if (conversation) {
    store.commit('conversations/UPDATE_CONVERSATION', {
      ...conversation,
      conversation_products: products.value,
    });
  }
};

// Coloca a conversa no funil/etapa escolhidos ANTES de salvar o produto:
// o sync de valor (deal_value/MRR) do backend só grava em mapping existente.
const syncFunnelPlacement = async () => {
  const { funnel_id: funnelId, stage_name: stageName } = form.value;
  if (!funnelId || !stageName) return;
  const mapping = getMappingForFunnel(funnelId);
  try {
    if (!mapping) {
      const response = await ConversationFunnelMappingsAPI.addToFunnel(
        props.conversationId,
        Number(funnelId),
        stageName
      );
      if (response.data.success) {
        const chat = currentConversation.value;
        if (chat) {
          store.commit('conversations/UPDATE_CONVERSATION', {
            ...chat,
            funnel_mappings: [
              ...(chat.funnel_mappings || []),
              response.data.mapping,
            ],
          });
        }
        window.dispatchEvent(
          new CustomEvent('conversation-added-to-funnel', {
            detail: {
              conversationId: props.conversationId,
              funnelId: Number(funnelId),
              stageName,
              mapping: response.data.mapping,
            },
          })
        );
      }
    } else if (mapping.stage_name !== stageName) {
      const response = await ConversationFunnelMappingsAPI.moveToStage(
        props.conversationId,
        mapping.id,
        stageName
      );
      if (response.data.success) {
        window.dispatchEvent(
          new CustomEvent('funnel-mapping-updated', {
            detail: {
              conversation_id: props.conversationId,
              funnel_id: mapping.funnel_id,
              stage_name: stageName,
              stage_label: response.data.mapping.stage_label,
              stage_color: response.data.mapping.stage_color,
              mapping: response.data.mapping,
            },
          })
        );
      }
    }
  } catch {
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.ADD_ERROR'));
  }
};

const saveProduct = async () => {
  if (
    !form.value.name ||
    !form.value.amount ||
    parseFloat(form.value.amount) <= 0
  ) {
    useAlert(t('PRODUCT.VALIDATION_ERROR'));
    return;
  }
  saving.value = true;
  try {
    await syncFunnelPlacement();
    const { stage_name: _stageName, ...productFields } = form.value;
    const productData = {
      ...productFields,
      amount: parseFloat(form.value.amount),
    };
    if (editingProduct.value) {
      const response = await conversationProductsAPI.update(
        props.conversationId,
        editingProduct.value.id,
        productData
      );
      const index = products.value.findIndex(
        p => p.id === editingProduct.value.id
      );
      if (index !== -1) products.value[index] = response.data;
      useAlert(t('PRODUCT.API_SUCCESS.UPDATE'));
    } else {
      const response = await conversationProductsAPI.create(
        props.conversationId,
        productData
      );
      products.value.unshift(response.data);
      useAlert(t('PRODUCT.API_SUCCESS.CREATE'));
    }
    updateConversationInStore();
    closeForm();
  } catch (error) {
    const errorMessage =
      error.response?.data?.errors?.join(', ') || t('PRODUCT.API_ERROR.SAVE');
    useAlert(errorMessage);
  } finally {
    saving.value = false;
  }
};

const editProduct = product => {
  editingProduct.value = product;
  form.value = {
    name: product.name,
    description: product.description || '',
    amount: parseFloat(product.amount) || 0,
    currency: product.currency || accountCurrency.value,
    funnel_id: product.funnel_id || getCurrentFunnelId(),
    stage_name:
      getMappingForFunnel(product.funnel_id || getCurrentFunnelId())
        ?.stage_name || null,
    product_id: product.product_id || null,
    quantity: product.quantity || 1,
    recurrence: product.recurrence || 'one_time',
  };
  showForm.value = true;
};

const deleteProduct = async productId => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PRODUCT.CONFIRM_DELETE'))) return;
  try {
    await conversationProductsAPI.delete(props.conversationId, productId);
    products.value = products.value.filter(p => p.id !== productId);
    useAlert(t('PRODUCT.API_SUCCESS.DELETE'));
    updateConversationInStore();
  } catch (error) {
    useAlert(t('PRODUCT.API_ERROR.DELETE'));
  }
};

const totalAmount = computed(() =>
  products.value.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
);

const mrrTotal = computed(() =>
  products.value.reduce((sum, p) => {
    const amount = parseFloat(p.amount) || 0;
    if (p.recurrence === 'monthly') return sum + amount;
    if (p.recurrence === 'yearly') return sum + amount / 12;
    return sum;
  }, 0)
);

onMounted(() => {
  fetchProducts();
  fetchFunnels();
  fetchCatalog();
});
</script>

<template>
  <div class="flex flex-col w-full gap-4 p-4">
    <div v-if="loading" class="flex justify-center py-8">
      <Spinner />
    </div>

    <template v-else>
      <!-- Cabeçalho -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ tp('PRODUCT.TITLE', 'Produtos e Serviços') }}
          </h4>
          <span
            v-if="products.length"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ products.length }}
          </span>
        </div>
        <Button
          v-if="!showForm"
          size="sm"
          color="teal"
          icon="i-lucide-plus"
          :label="tp('PRODUCT.ADD', 'Adicionar produto')"
          @click="openAdd"
        />
      </div>

      <!-- Formulário -->
      <div
        v-if="showForm"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <!-- Seletor do catálogo (sempre visível) -->
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ tp('PRODUCT.CATALOG_LABEL', 'Produto do catálogo') }}
          </span>
          <select
            v-model="form.product_id"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            @change="onCatalogSelect"
          >
            <option :value="null">
              {{ tp('PRODUCT.CATALOG_MANUAL', 'Item manual (digite abaixo)') }}
            </option>
            <option
              v-for="item in catalogProducts"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }} — {{ formatCurrency(item.unit_price)
              }}{{ recurrenceSuffix(item.recurrence) }}
            </option>
          </select>
          <span v-if="!catalogProducts.length" class="text-xs text-n-slate-10">
            {{
              tp(
                'PRODUCT.CATALOG_EMPTY_HINT',
                'Cadastre produtos em Configurações › Produtos para reutilizar aqui.'
              )
            }}
          </span>
        </label>

        <!-- Nome -->
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ tp('PRODUCT.NAME', 'Nome') }} *
          </span>
          <input
            v-model="form.name"
            data-testid="product-name"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            :placeholder="t('PRODUCT.NAME_PLACEHOLDER')"
          />
        </label>

        <!-- Valor + Quantidade -->
        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ tp('PRODUCT.AMOUNT', 'Valor') }} *
            </span>
            <input
              v-model.number="form.amount"
              data-testid="product-amount"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ tp('PRODUCT.QUANTITY', 'Quantidade') }}
            </span>
            <input
              v-model.number="form.quantity"
              type="number"
              min="1"
              step="1"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              @input="onQuantityChange"
            />
          </label>
        </div>

        <!-- Recorrência + Moeda -->
        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ tp('PRODUCT.RECURRENCE_LABEL', 'Recorrência') }}
            </span>
            <select
              v-model="form.recurrence"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            >
              <option value="one_time">{{ recurrenceLabels.one_time }}</option>
              <option value="monthly">{{ recurrenceLabels.monthly }}</option>
              <option value="yearly">{{ recurrenceLabels.yearly }}</option>
            </select>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ tp('PRODUCT.CURRENCY', 'Moeda') }}
            </span>
            <select
              v-model="form.currency"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            >
              <option value="BRL">
                {{ tp('PRODUCT.CURRENCY_OPTIONS.BRL', 'Real (BRL)') }}
              </option>
              <option value="USD">
                {{ tp('PRODUCT.CURRENCY_OPTIONS.USD', 'Dólar (USD)') }}
              </option>
              <option value="EUR">
                {{ tp('PRODUCT.CURRENCY_OPTIONS.EUR', 'Euro (EUR)') }}
              </option>
            </select>
          </label>
        </div>

        <!-- Funil + Etapa -->
        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{
              tp('KANBAN.FUNNEL', 'Funil')
            }}</span>
            <select
              v-model="form.funnel_id"
              data-testid="product-funnel"
              :disabled="loadingFunnels"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
              @change="onFunnelSelect"
            >
              <option :value="null">
                {{ t('KANBAN.SELECT_FUNNEL_OPTIONAL') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
          </label>
          <label v-if="form.funnel_id" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">{{
              tp('PRODUCT.STAGE', 'Etapa do funil')
            }}</span>
            <select
              v-model="form.stage_name"
              data-testid="product-stage"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            >
              <option :value="null">
                {{ t('KANBAN.SELECT_STAGE') }}
              </option>
              <option
                v-for="stage in funnelStages"
                :key="stage.name"
                :value="stage.name"
              >
                {{ stage.label }}
              </option>
            </select>
          </label>
        </div>

        <!-- Descrição -->
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ tp('PRODUCT.DESCRIPTION', 'Descrição') }}
          </span>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            :placeholder="t('PRODUCT.DESCRIPTION_PLACEHOLDER')"
          />
        </label>

        <div v-if="form.amount > 0" class="text-sm font-medium text-n-teal-11">
          {{ formatCurrency(form.amount, form.currency)
          }}{{ recurrenceSuffix(form.recurrence) }}
        </div>

        <div class="flex justify-end gap-2 pt-1">
          <Button
            variant="clear"
            size="sm"
            :label="tp('COMMON.CANCEL', 'Cancelar')"
            @click="closeForm"
          />
          <Button
            data-testid="product-save"
            color="teal"
            size="sm"
            :is-loading="saving"
            :label="
              editingProduct
                ? tp('COMMON.UPDATE', 'Atualizar')
                : tp('COMMON.SAVE', 'Salvar')
            "
            @click="saveProduct"
          />
        </div>
      </div>

      <!-- Lista de produtos -->
      <div v-if="products.length" class="flex flex-col divide-y divide-n-weak">
        <div
          v-for="product in products"
          :key="product.id"
          class="flex items-center gap-3 py-3 group"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ product.name }}
              </span>
              <span
                v-if="product.recurrence && product.recurrence !== 'one_time'"
                class="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-n-teal-3 text-n-teal-11 shrink-0"
              >
                {{ recurrenceLabels[product.recurrence] }}
              </span>
            </div>
            <div
              v-if="product.description"
              class="text-xs truncate text-n-slate-10"
            >
              {{ product.description }}
            </div>
          </div>
          <div class="text-right shrink-0">
            <div class="text-sm font-semibold text-n-slate-12 tabular-nums">
              {{ formatCurrency(product.amount, product.currency) }}
            </div>
            <div
              v-if="product.quantity && product.quantity > 1"
              class="text-xs text-n-slate-10"
            >
              {{ `${product.quantity}×` }}
            </div>
          </div>
          <div
            class="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100"
          >
            <Button
              variant="ghost"
              size="xs"
              icon="i-lucide-pencil"
              @click="editProduct(product)"
            />
            <Button
              variant="ghost"
              size="xs"
              color="ruby"
              icon="i-lucide-trash-2"
              @click="deleteProduct(product.id)"
            />
          </div>
        </div>

        <!-- Totais -->
        <div class="flex flex-col gap-1 pt-3">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-11">
              {{ tp('DEAL_RECORD.INSIGHTS.TOTAL', 'Total') }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
              {{ formatCurrency(totalAmount) }}
            </span>
          </div>
          <div v-if="mrrTotal > 0" class="flex items-center justify-between">
            <span class="text-xs font-medium text-n-slate-10">
              {{ tp('PRODUCT.MRR_LABEL', 'Recorrente (MRR)') }}
            </span>
            <span class="text-sm font-semibold text-n-teal-11 tabular-nums">
              {{ `${formatCurrency(mrrTotal)}/mês` }}
            </span>
          </div>
        </div>
      </div>

      <!-- Vazio -->
      <div
        v-else-if="!showForm"
        class="flex flex-col items-center gap-2 py-8 text-center"
      >
        <span class="text-2xl i-lucide-package text-n-slate-8" />
        <p class="text-sm text-n-slate-10">
          {{ tp('PRODUCT.NO_PRODUCTS', 'Nenhum produto cadastrado') }}
        </p>
      </div>
    </template>
  </div>
</template>
